import { FormEvent, useEffect, useState } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import { RootState } from "../../store/combineReducer";
import { RefreshCw, FileSpreadsheet } from "lucide-react";
import * as XLSX from "xlsx-js-style";
import LavorisService from "../../services/LavorisService";
import "./cuenta_corriente.scss";

type Movimiento = {
  id: string | number;
  fecha: string;
  fechaVencimiento: string;
  idComprobante: number | string;
  saldo: number;
  diasAtraso?: number | null;
};

const formatFecha = (value: string) =>
  value ? moment(value).format("DD/MM/YYYY") : "---";

const formatMonto = (value: number) =>
  `$${Number(value).toLocaleString("es-AR")}`;

const toMovimiento = (row: any): Movimiento => ({
  id: row?.DT_RowId ?? row?.IDCuentaCorriente ?? row?.IDComprobante,
  fecha: row?.Fecha ?? "",
  fechaVencimiento: row?.FechaVto ?? "",
  idComprobante: row?.IDComprobante ?? "---",
  saldo: Number(row?.MontoTotal ?? 0),
  diasAtraso:
    row?.DiasAtraso == null || row?.DiasAtraso === ""
      ? null
      : Number(row.DiasAtraso),
});

const readRows = (block: any): Movimiento[] => {
  const rows = block?.Data?.DT?.data;
  return Array.isArray(rows) ? rows.map(toMovimiento) : [];
};

const readExtraSaldo = (block: any): number | null => {
  const saldo = block?.Data?.Extra?.[0]?.Saldo;
  if (saldo == null || saldo === "" || Number.isNaN(Number(saldo))) return null;
  return Number(saldo);
};

const diasAtrasoClass = (dias: number | null | undefined, siempreRojo?: boolean) => {
  if (dias == null || Number.isNaN(dias)) return undefined;
  if (siempreRojo || dias >= 0) return "dias-atraso dias-atraso--rojo";
  return "dias-atraso dias-atraso--verde";
};

const downloadMovimientosExcel = (
  fileName: string,
  rows: Movimiento[],
  total: number | null,
) => {
  const headers = [
    "Fecha",
    "Fecha de vencimiento",
    "ID de comprobante",
    "Monto",
    "Días de atraso",
  ];
  const excelRows = rows.map((row) => [
    formatFecha(row.fecha),
    formatFecha(row.fechaVencimiento),
    row.idComprobante,
    formatMonto(row.saldo),
    row.diasAtraso ?? 0,
  ]);
  if (total != null) {
    excelRows.push(["Total", "", "", formatMonto(total), ""]);
  }

  const ws = XLSX.utils.aoa_to_sheet([headers, ...excelRows]);
  rows.forEach((row, index) => {
    if (row.diasAtraso == null || Number.isNaN(row.diasAtraso)) return;
    const cell = ws[XLSX.utils.encode_cell({ r: index + 1, c: 4 })];
    if (!cell) return;
    cell.s = {
      font: {
        bold: true,
        color: { rgb: row.diasAtraso < 0 ? "1A7F37" : "D93020" },
      },
    };
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, fileName.split("_")[0].slice(0, 31));
  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName.endsWith(".xlsx") ? fileName : `${fileName}.xlsx`;
  link.click();
  URL.revokeObjectURL(url);
};

const MovimientoTable = ({
  title,
  rows,
  total,
  onDownloadFactura,
  downloadingId,
  diasAtrasoSiempreRojo,
  collapsible,
  onRefresh,
  refreshing,
  excelFileName,
  includeExcelTotal,
}: {
  title: string;
  rows: Movimiento[];
  total: number | null;
  onDownloadFactura: (idComprobante: number | string) => void;
  downloadingId: number | string | null;
  diasAtrasoSiempreRojo?: boolean;
  collapsible?: boolean;
  onRefresh?: () => void;
  refreshing?: boolean;
  excelFileName?: string;
  includeExcelTotal?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const showTable = !collapsible || open;
  const totalMostrado = total == null ? null : Math.max(0, total);

  return (
  <section className="cuenta-corriente-section">
    <div className="modern-table-container">
      <div className="table-toolbar">
        {collapsible ? (
          <button
            type="button"
            className="toolbar-left cuenta-corriente-accordion-toggle"
            aria-expanded={open}
            onClick={() => setOpen((current) => !current)}
          >
            <span className={`cuenta-corriente-chevron ${open ? "is-open" : ""}`} />
            <h2 className="table-title">{title}</h2>
            {totalMostrado != null && (
              <span className="cuenta-corriente-total">{formatMonto(totalMostrado)}</span>
            )}
          </button>
        ) : (
          <div className="toolbar-left">
            <h2 className="table-title">{title}</h2>
            {totalMostrado != null && (
              <span className="cuenta-corriente-total">{formatMonto(totalMostrado)}</span>
            )}
          </div>
        )}
        <div className="toolbar-right">
          <span className="cuenta-corriente-count">
            Cantidad de registros: {rows.length}
          </span>
          {excelFileName && (
            <button
              type="button"
              className="cuenta-corriente-refresh"
              onClick={() =>
                downloadMovimientosExcel(
                  excelFileName,
                  rows,
                  includeExcelTotal ? totalMostrado : null,
                )
              }
              aria-label="Descargar excel"
              title="Descargar excel"
            >
              <FileSpreadsheet size={18} />
            </button>
          )}
          {onRefresh && (
            <button
              type="button"
              className="cuenta-corriente-refresh"
              onClick={onRefresh}
              disabled={refreshing}
              aria-label="Actualizar"
            >
              <RefreshCw size={18} className={refreshing ? "is-spinning" : ""} />
            </button>
          )}
        </div>
      </div>
      {showTable && (
      <div className="table-wrapper">
        <table className="modern-table">
          <thead>
            <tr>
              <th className="table-header">Fecha</th>
              <th className="table-header">Fecha de vencimiento</th>
              <th className="table-header">ID de comprobante</th>
              <th className="table-header">Monto</th>
              <th className="table-header">Días de atraso</th>
              <th className="table-header" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr className="table-row">
                <td className="table-cell empty-cell" colSpan={6}>
                  No hay comprobantes
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr className="table-row" key={row.id}>
                  <td className="table-cell">{formatFecha(row.fecha)}</td>
                  <td className="table-cell">{formatFecha(row.fechaVencimiento)}</td>
                  <td className="table-cell">{row.idComprobante}</td>
                  <td className="table-cell">{formatMonto(row.saldo)}</td>
                  <td className={`table-cell ${diasAtrasoClass(row.diasAtraso, diasAtrasoSiempreRojo) ?? ""}`}>
                    {row.diasAtraso ?? 0}
                  </td>
                  <td className="table-cell">
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={downloadingId === row.idComprobante}
                      onClick={() => onDownloadFactura(row.idComprobante)}
                    >
                      {downloadingId === row.idComprobante ? "Descargando..." : "Descargar factura"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      )}
    </div>
  </section>
  );
};

const PAGE_TABS = [
  { id: "estado", label: "Estado de cuenta" },
  { id: "facturas", label: "Facturas" },
];

const firstDayOfCurrentMonth = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${now.getFullYear()}-${month}-01`;
};

const lastDayOfCurrentMonth = () => {
  const now = new Date();
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const month = String(last.getMonth() + 1).padStart(2, "0");
  const day = String(last.getDate()).padStart(2, "0");
  return `${last.getFullYear()}-${month}-${day}`;
};

const FacturasTab = ({
  idClienteProveedor,
  onDownloadFactura,
  downloadingId,
}: {
  idClienteProveedor?: string;
  onDownloadFactura: (idComprobante: number | string) => void;
  downloadingId: number | string | null;
}) => {
  const LavorisInstance = LavorisService.getInstance();
  const [fechaDesde, setFechaDesde] = useState(firstDayOfCurrentMonth);
  const [fechaHasta, setFechaHasta] = useState(lastDayOfCurrentMonth);
  const [rows, setRows] = useState<Movimiento[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const buscar = async (event: FormEvent) => {
    event.preventDefault();
    if (!idClienteProveedor || !fechaDesde || !fechaHasta) return;

    setLoading(true);
    setSearched(true);
    try {
      const response = await LavorisInstance.CompanyServices.getCuentaCorrientePorFechas(
        idClienteProveedor,
        fechaDesde,
        fechaHasta,
      );
      setRows(readRows(response));
      setTotal(readExtraSaldo(response));
    } catch (error) {
      console.error(error);
      setRows([]);
      setTotal(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <form className="cuenta-corriente-filtros" onSubmit={buscar}>
        <label>
          Desde
          <input
            type="date"
            value={fechaDesde}
            onChange={(event) => setFechaDesde(event.target.value)}
            required
          />
        </label>
        <label>
          Hasta
          <input
            type="date"
            value={fechaHasta}
            onChange={(event) => setFechaHasta(event.target.value)}
            required
          />
        </label>
        <button type="submit" className="btn btn-primary" disabled={!idClienteProveedor || loading}>
          {loading ? "Buscando..." : "Buscar"}
        </button>
      </form>
      {searched && (
        loading ? (
          <p>Cargando...</p>
        ) : (
          <MovimientoTable
            title="Facturas"
            rows={rows}
            total={total}
            onDownloadFactura={onDownloadFactura}
            downloadingId={downloadingId}
            excelFileName={`Facturas_${fechaDesde}_${fechaHasta}`}
          />
        )
      )}
    </div>
  );
};

const CuentaCorrienteContent = () => {
  const LavorisInstance = LavorisService.getInstance();
  const { companies } = useSelector((state: RootState) => state.user);
  const companyCode = companies?.[0]?.managerId;
  const [atrasados, setAtrasados] = useState<Movimiento[]>([]);
  const [sinCancelar, setSinCancelar] = useState<Movimiento[]>([]);
  const [totalAtrasado, setTotalAtrasado] = useState<number | null>(null);
  const [totalSinCancelar, setTotalSinCancelar] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState<"vencido" | "a-cancelar" | null>(null);
  const [currentTab, setCurrentTab] = useState(PAGE_TABS[0].id);
  const [downloadingId, setDownloadingId] = useState<number | string | null>(null);

  useEffect(() => {
    if (currentTab !== "estado") return;
    if (!companyCode) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const response = await LavorisInstance.CompanyServices.getCompanyCuentaCorriente(
          companyCode,
          controller.signal,
        );
        if (!cancelled) {
          setAtrasados(readRows(response?.data_late));
          setSinCancelar(readRows(response?.data_to_cancel));
          setTotalAtrasado(readExtraSaldo(response?.data_late));
          setTotalSinCancelar(readExtraSaldo(response?.data_to_cancel));
        }
      } catch (error) {
        if (!cancelled) {
          setAtrasados([]);
          setSinCancelar([]);
          setTotalAtrasado(null);
          setTotalSinCancelar(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [companyCode, currentTab, LavorisInstance]);

  const refreshLista = async (lista: "vencido" | "a-cancelar") => {
    if (!companyCode || refreshing) return;
    setRefreshing(lista);
    try {
      const response = await LavorisInstance.CompanyServices.getCompanyCuentaCorriente(companyCode);
      if (lista === "vencido") {
        setAtrasados(readRows(response?.data_late));
        setTotalAtrasado(readExtraSaldo(response?.data_late));
      } else {
        setSinCancelar(readRows(response?.data_to_cancel));
        setTotalSinCancelar(readExtraSaldo(response?.data_to_cancel));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setRefreshing(null);
    }
  };

  const downloadFactura = async (idComprobante: number | string) => {
    if (downloadingId != null) return;
    setDownloadingId(idComprobante);
    try {
      await LavorisInstance.CompanyServices.downloadComprobanteFactura(idComprobante);
    } catch (error) {
      console.error(error);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="main-container">
      <div className="atendoo-card">
        <div className="users-roles-header">
          <h6 className="subtitle">Cuenta corriente</h6>
        </div>
        <div className="tabs-container mb-3">
          <nav className="tabs-nav">
            {PAGE_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`tab-button ${currentTab === tab.id ? "active" : ""}`}
                onClick={() => setCurrentTab(tab.id)}
              >
                <span className="tab-label">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
        {currentTab === "facturas" ? (
          <FacturasTab
            idClienteProveedor={companyCode}
            onDownloadFactura={downloadFactura}
            downloadingId={downloadingId}
          />
        ) : loading ? (
          <div className="cuenta-corriente-loading" role="status">
            <span className="cuenta-corriente-loading-spinner" />
            <span>Cargando...</span>
          </div>
        ) : (
          <>
            <MovimientoTable
              title="Saldo vencido"
              rows={atrasados}
              total={totalAtrasado}
              onDownloadFactura={downloadFactura}
              downloadingId={downloadingId}
              diasAtrasoSiempreRojo
              collapsible
              onRefresh={() => refreshLista("vencido")}
              refreshing={refreshing === "vencido"}
              excelFileName="cuenta_corriente_vencido"
              includeExcelTotal
            />
            <MovimientoTable
              title="Saldo a cancelar"
              rows={sinCancelar}
              total={totalSinCancelar}
              onDownloadFactura={downloadFactura}
              downloadingId={downloadingId}
              collapsible
              onRefresh={() => refreshLista("a-cancelar")}
              refreshing={refreshing === "a-cancelar"}
              excelFileName="cuenta_corriente_a_cancelar"
              includeExcelTotal
            />
          </>
        )}
      </div>
    </div>
  );
};

const CuentaCorrientePage = () => {
  const chosenRole = useSelector((state: RootState) => state.user.currentRole);

  const ROLE_VIEWS: Record<string, JSX.Element> = {
    COMPANY_ADMIN: <CuentaCorrienteContent />,
    RRHH: <CuentaCorrienteContent />,
  };

  return ROLE_VIEWS[chosenRole] ?? <></>;
};

export default CuentaCorrientePage;
